import { useEffect, useMemo, useState } from "react";
import Icon from "../components/Icon";
import { Badge, BtnPrimary } from "../components/Atoms";
import { puedeCrearProyectos } from "../utils";

// Los protocolos de actuacion: flujos de trabajo escritos una vez para repetirse
// muchas.
//
// Un protocolo ES un proyecto con una marca, asi que se abre y se edita con el
// mismo lienzo que cualquier otro. Esta pagina no es un editor: es el estante
// donde estan, con las dos unicas acciones que le son propias —arrancar un
// proyecto desde uno, y abrirlo para retocarlo—.
//
// Lo que NO se pinta aqui, aunque venga en los datos: progreso, horas reales,
// fechas ni estado. Un protocolo no se ejecuta, asi que todo eso vale cero y
// ensenarlo solo invitaria a leerlo como un proyecto parado.

const inS = { padding: "6px 9px", borderRadius: 8, border: "1px solid var(--border)",
  fontSize: 12, fontFamily: "var(--font)", background: "var(--surface)", color: "var(--text)" };

function Tarjeta({ p, onUsar, onAbrir, puedeUsar }) {
  const pasos = Number(p.tareas || 0);
  const plan  = p.plan_horas ?? null;

  return (
    <div className="card-hover fu"
      style={{ background: "var(--surface)", border: "1px solid var(--border)",
        borderRadius: 13, padding: 14, borderTop: `3px solid ${p.color || "#8b5cf6"}`,
        display: "flex", flexDirection: "column", gap: 10, minHeight: 156 }}>

      <div style={{ display: "flex", alignItems: "flex-start", gap: 9 }}>
        <div style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0,
          background: (p.color || "#8b5cf6") + "18", display: "flex",
          alignItems: "center", justifyContent: "center" }}>
          <Icon name={p.icon || "journal-text"} size={15} color={p.color || "#8b5cf6"} />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 13.5, color: "var(--text)",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {p.name}
          </div>
          <div style={{ fontSize: 10.5, color: "var(--text3)", marginTop: 2 }}>
            {p.team_name || "De toda la casa"}
          </div>
        </div>
      </div>

      {p.description && (
        <div style={{ fontSize: 11.5, color: "var(--text3)", lineHeight: 1.45,
          overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical" }}>
          {p.description}
        </div>
      )}

      <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 9 }}>
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
          <Badge label={`${pasos} ${pasos === 1 ? "paso" : "pasos"}`}
            color="var(--text2)" bg="var(--surface2)" />
          {/* El plan de horas es lo unico que un protocolo sabe de duracion: no
              tiene fechas, pero si cuanto cuesta cada paso. */}
          {plan ? <Badge label={`${plan} h de plan`} color="var(--text2)" bg="var(--surface2)" />
                : <Badge label="Sin estimar" color="var(--text3)" bg="var(--bg)" />}
        </div>

        <div style={{ display: "flex", gap: 6 }}>
          {puedeUsar && (
            <BtnPrimary onClick={() => onUsar(p)} style={{ flex: 1, justifyContent: "center",
              padding: "7px 10px", fontSize: 12 }}>
              <Icon name="plus-lg" size={11} color="#fff" />Crear proyecto
            </BtnPrimary>
          )}
          <button className="btn" onClick={() => onAbrir(p)}
            title="Ver y editar los pasos de este protocolo"
            style={{ ...inS, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}>
            <Icon name="diagram-3" size={11} color="var(--text2)" />
            {puedeUsar ? "" : "Ver el flujo"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PlantillasPage({
  plantillas = [], currentUser, onCargar, onUsar, onAbrir,
}) {
  const [q, setQ] = useState("");
  // Se cargan al entrar en la pestaña y no con el resto de la aplicacion: son
  // pocas y solo hacen falta aqui.
  useEffect(() => { onCargar?.(); }, []);  // eslint-disable-line

  const puedeUsar = puedeCrearProyectos(currentUser);

  const lista = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return plantillas;
    return plantillas.filter(p =>
      (p.name || "").toLowerCase().includes(t)
      || (p.description || "").toLowerCase().includes(t)
      || (p.team_name || "").toLowerCase().includes(t));
  }, [plantillas, q]);

  return (
    <div>
      <div className="filters-bar bar-wrap" style={{ display: "flex", gap: 7, alignItems: "center",
        marginBottom: 14, flexWrap: "wrap" }}>
        <div style={{ position: "relative" }}>
          <input aria-label="Buscar protocolo" value={q} onChange={e => setQ(e.target.value)}
            placeholder="Buscar protocolo..."
            style={{ ...inS, width: 210, paddingLeft: 28 }} />
          <Icon name="search" size={11} color="var(--text3)"
            style={{ position: "absolute", left: 9, top: 8 }} />
        </div>
        <span style={{ fontSize: 11.5, color: "var(--text3)" }}>
          {lista.length}{lista.length !== plantillas.length ? ` de ${plantillas.length}` : ""}
          {lista.length === 1 ? " protocolo" : " protocolos"}
        </span>
      </div>

      {!plantillas.length && (
        <div style={{ padding: "48px 20px", textAlign: "center" }}>
          <Icon name="journal-text" size={30} color="var(--text3)" />
          <div style={{ fontWeight: 700, fontSize: 15, marginTop: 12, color: "var(--text2)" }}>
            Todavia no hay ningun protocolo
          </div>
          {/* Se explica COMO se crea uno, porque no se hace desde aqui: un
              protocolo nace de un proyecto que ya has hecho y que merece
              repetirse, no de un formulario en blanco. */}
          <div style={{ fontSize: 12.5, color: "var(--text3)", marginTop: 6, lineHeight: 1.6,
            maxWidth: 430, marginLeft: "auto", marginRight: "auto" }}>
            Un protocolo es un proyecto que merece repetirse. Abre uno que ya tengas hecho
            y pulsa <b>Guardar como protocolo</b>: se queda con sus pasos, sus conexiones y
            quien hace cada cosa, y cada proyecto que salga de el nace con todo eso puesto.
          </div>
        </div>
      )}

      {!!plantillas.length && !lista.length && (
        <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--text3)",
          fontSize: 13 }}>
          Ningun protocolo coincide con «{q}».
        </div>
      )}

      {!!lista.length && (
        <div style={{ display: "grid", gap: 12,
          gridTemplateColumns: "repeat(auto-fill,minmax(276px,1fr))" }}>
          {lista.map(p => (
            <Tarjeta key={p.id} p={p} puedeUsar={puedeUsar}
              onUsar={onUsar} onAbrir={onAbrir} />
          ))}
        </div>
      )}
    </div>
  );
}
